const parseAbilityLoss = (v) => {
    if (!isObj(v) || !isInt(v.base, 0, 100) || !isInt(v.per, 1, 100) || (v.damage_as !== 'score' && v.damage_as !== 'penalty') || !isObj(v.abilities))
        return undefined;
    const abilities = {};
    for (const [k, a] of Object.entries(v.abilities)) {
        if (!isObj(a) || !isText(a.label, 40) || !isText(a.zero, 40) || !Array.isArray(a.mods) || a.mods.length > 4)
            return undefined;
        const mods = a.mods.flatMap((m) => {
            if (!isObj(m) || (m.target !== 'attack' && m.target !== 'ac' && m.target !== 'damage' && m.target !== 'save' && m.target !== 'initiative'))
                return [];
            return [{ target: m.target, ...(m.only === 'melee' || m.only === 'ranged' ? { only: m.only } : {}), ...(isText(m.which, 40) ? { which: m.which } : {}) }];
        });
        abilities[k] = { label: a.label, mods, zero: a.zero };
    }
    return { base: v.base, per: v.per, damageAs: v.damage_as, ...(isText(v.damage_zero, 40) ? { damageZero: v.damage_zero } : {}), abilities };
};
/** An ability as the rule's key: a stat block says `con`, a sheet's word may be its name. Null when the system does not list it. */
export function abilityKeyOf(rule, word) {
    const w = word.trim().toLowerCase();
    return Object.keys(rule.abilities).find((k) => k === w || rule.abilities[k].label.toLowerCase() === w) ?? null;
}
/** One ability's loss worked out. A score not known counts as even, so each `per` points lost is a point of modifier. */
export function abilityLossRow(rule, key, loss, known) {
    const a = rule.abilities[key];
    const drain = loss.drain ?? 0;
    const damage = loss.damage ?? 0;
    const off = rule.damageAs === 'score' ? drain + damage : drain;
    const penalty = rule.damageAs === 'penalty' ? Math.floor(damage / rule.per) : 0;
    const mod = (n) => Math.floor((n - rule.base) / rule.per);
    const score = known === null ? null : Math.max(0, known - off);
    const drop = (known === null ? Math.ceil(off / rule.per) : mod(known) - mod(known - off)) + penalty;
    const bottom = score !== null && score <= 0;
    const reached = score !== null && rule.damageAs === 'penalty' && damage >= score;
    const zero = bottom ? a?.zero ?? null : reached ? (a?.zero === 'dead' ? 'dead' : rule.damageZero ?? null) : null;
    return { key, label: a?.label ?? key, lost: damage + drain, score, drop, zero };
}
/** The numbers a modifier that fell takes off, as a table effect's. */
export function abilityLossMods(rule, row) {
    if (row.drop <= 0)
        return [];
    return (rule.abilities[row.key]?.mods ?? []).flatMap((m) => (m.target === 'initiative' ? [] : [{ target: m.target, value: -row.drop, ...(m.only ? { only: m.only } : {}), ...(m.which ? { which: m.which } : {}) }]));
}
/**
 * The ability scores an effect raises or lowers (an `ability` number; qa-runs#1442: Cat's Grace's Dexterity +4) as the
 * numbers each score feeds (`abilities[key].mods`): per score, the best of each bonus type and every penalty and
 * untyped number added, so two enhancement bonuses do not stack; a score not known counts as even, so each `per` points
 * is a point of modifier. One synthetic effect per score whose modifier moves, read where effects are read; nothing is
 * stored, so it goes with the last effect that raised it. Empty without the rule or any `ability` number.
 */
export function abilityEffects(effects, rule) {
    const out = [];
    for (const { key, net, change } of abilityChanges(effects, rule)) {
        const fed = rule.abilities[key].mods.flatMap((m) => (m.target === 'initiative' ? [] : [{ target: m.target, value: change, ...(m.only ? { only: m.only } : {}), ...(m.which ? { which: m.which } : {}) }]));
        if (fed.length)
            out.push({ label: `${rule.abilities[key].label} ${net > 0 ? '+' : ''}${net}`, mods: fed });
    }
    return out;
}
/**
 * What the effects' raised or lowered scores add to the initiative bonus (qa-runs#1442: Cat's Grace's Dexterity), by
 * the scores whose `mods` name `initiative`: added to a throw as it is made, so an initiative already rolled stays.
 */
export function abilityInitiative(effects, rule) {
    return abilityChanges(effects, rule).reduce((n, { key, change }) => n + (rule.abilities[key].mods.some((m) => m.target === 'initiative') ? change : 0), 0);
}
/** Each score's net change from the effects' `ability` numbers (best of each bonus type; penalties and untyped added) and what it moves its modifier by; scores whose modifier does not move are left out. */
function abilityChanges(effects, rule) {
    if (!rule)
        return [];
    const by = new Map();
    for (const e of effects)
        for (const m of e.mods ?? [])
            if (m.target === 'ability' && m.which && rule.abilities[m.which])
                by.set(m.which, [...(by.get(m.which) ?? []), m]);
    const out = [];
    for (const [key, mods] of by) {
        const best = new Map();
        let net = 0;
        for (const m of mods) {
            if (!m.type || m.value < 0)
                net += m.value;
            else
                best.set(m.type, Math.max(best.get(m.type) ?? 0, m.value));
        }
        for (const v of best.values())
            net += v;
        const change = Math.floor(net / rule.per);
        if (change)
            out.push({ key, net, change });
    }
    return out;
}
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v, lo, hi) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
const isText = (v, max) => typeof v === 'string' && v.trim().length > 0 && v.length <= max;
export function parseSpecialRules(v) {
    if (!isObj(v))
        return null;
    const b = v.breath_recharge;
    if (!isObj(b) || !isInt(b.count, 1, 10) || !isInt(b.sides, 2, 100))
        return null;
    if (!isInt(v.poison_rounds, 1, 100) || !isText(v.drain_condition, 40) || !isText(v.aura_save, 40) || !isObj(v.follow))
        return null;
    const follow = {};
    for (const [k, m] of Object.entries(v.follow)) {
        if (!isText(m, 40))
            return null;
        follow[k] = m;
    }
    const abilityLoss = parseAbilityLoss(v.ability_loss);
    return { breathRecharge: { count: b.count, sides: b.sides }, poisonRounds: v.poison_rounds, drainCondition: v.drain_condition, auraSave: v.aura_save, follow, ...(abilityLoss ? { abilityLoss } : {}), ...(isInt(v.image_near_miss, 1, 100) ? { imageNearMiss: v.image_near_miss } : {}) };
}
const DICE_RE = /^(\d{1,3})d(\d{1,3})([+-]\d{1,3})?$/;
const isDice = (v) => typeof v === 'string' && DICE_RE.test(v);
const words = (v) => (Array.isArray(v) && v.every((w) => isText(w, 40)) ? v.slice(0, 8) : undefined);
const on = (v) => { const w = words(v.on); return w?.length ? { on: w } : {}; };
const dcOf = (v) => (isInt(v.dc, 1, 100) ? { dc: v.dc } : {});
const hurt = (v) => (isObj(v) && isDice(v.dice) && isText(v.ability, 12) ? { dice: v.dice, ability: v.ability } : null);
const hit = (v) => {
    if (!isObj(v) || !isDice(v.damage))
        return null;
    const x = isObj(v.extra) && isDice(v.extra.dice) ? { extra: { dice: v.extra.dice, ...(isText(v.extra.type, 24) ? { type: v.extra.type } : {}) } } : {};
    return { damage: v.damage, ...x, ...(isText(v.of, 40) ? { of: v.of } : {}) };
};
/** The register's value, read strictly part by part: a part out of shape is left out, and all out of shape is null. */
export function parseSpecials(v) {
    if (!isObj(v))
        return null;
    const s = {};
    if (Array.isArray(v.breath)) {
        const list = v.breath.flatMap((b) => {
            if (!isObj(b) || (b.shape !== 'cone' && b.shape !== 'line') || !isInt(b.feet, 5, 1000) || !isDice(b.damage) || !isText(b.save, 40) || !isInt(b.dc, 1, 100))
                return [];
            const r = isObj(b.recharge) ? { ...(isDice(b.recharge.dice) ? { dice: b.recharge.dice } : {}), ...(isInt(b.recharge.rounds, 1, 1000) ? { rounds: b.recharge.rounds } : {}), ...(isInt(b.recharge.per_day, 1, 100) ? { per_day: b.recharge.per_day } : {}) } : null;
            return [{ shape: b.shape, feet: b.feet, damage: b.damage, ...(isText(b.type, 24) ? { type: b.type } : {}), save: b.save, on_save: b.on_save === 'none' ? 'none' : 'half', dc: b.dc, ...(r && Object.keys(r).length ? { recharge: r } : {}) }];
        });
        if (list.length)
            s.breath = list.slice(0, 4);
    }
    if (Array.isArray(v.poison)) {
        const list = v.poison.flatMap((p) => {
            if (!isObj(p) || !isText(p.save, 40) || !isInt(p.dc, 1, 100))
                return [];
            const a = hurt(p.initial);
            const b = hurt(p.secondary);
            return a && b ? [{ save: p.save, dc: p.dc, initial: a, secondary: b, ...on(p) }] : [];
        });
        if (list.length)
            s.poison = list.slice(0, 4);
    }
    if (isInt(v.fast_healing, 1, 1000))
        s.fast_healing = v.fast_healing;
    if (isObj(v.regeneration) && isInt(v.regeneration.amount, 1, 1000))
        s.regeneration = { amount: v.regeneration.amount, ...(isText(v.regeneration.except, 80) ? { except: v.regeneration.except } : {}) };
    if (isObj(v.grab))
        s.grab = on(v.grab);
    if (isObj(v.trip))
        s.trip = on(v.trip);
    if (v.pounce === true)
        s.pounce = true;
    const c = hit(v.constrict);
    if (c)
        s.constrict = c;
    const r = hit(v.rend);
    if (r)
        s.rend = r;
    if (isObj(v.energy_drain) && isInt(v.energy_drain.levels, 1, 20))
        s.energy_drain = { levels: v.energy_drain.levels, ...dcOf(v.energy_drain), ...on(v.energy_drain) };
    if (Array.isArray(v.ability)) {
        const list = v.ability.flatMap((a) => (isObj(a) && isDice(a.dice) && isText(a.ability, 12) ? [{ dice: a.dice, ability: a.ability, how: a.how === 'drain' ? 'drain' : 'damage', ...dcOf(a), ...on(a) }] : []));
        if (list.length)
            s.ability = list.slice(0, 4);
    }
    if (isObj(v.fear_aura) && isText(v.fear_aura.label, 40) && isInt(v.fear_aura.feet, 5, 1000) && isInt(v.fear_aura.dc, 1, 100))
        s.fear_aura = { label: v.fear_aura.label, feet: v.fear_aura.feet, dc: v.fear_aura.dc };
    if (isObj(v.spell_like) && Array.isArray(v.spell_like.groups)) {
        const groups = v.spell_like.groups.flatMap((g) => {
            if (!isObj(g) || !Array.isArray(g.spells) || !(g.per_day === null || isInt(g.per_day, 1, 100)))
                return [];
            const spells = g.spells.flatMap((x) => (isObj(x) && isText(x.name, 80) ? [{ name: x.name, ...dcOf(x) }] : []));
            return spells.length ? [{ per_day: g.per_day, spells: spells.slice(0, 24) }] : [];
        });
        if (groups.length)
            s.spell_like = { ...(isInt(v.spell_like.caster_level, 1, 100) ? { caster_level: v.spell_like.caster_level } : {}), groups: groups.slice(0, 8) };
    }
    return Object.keys(s).length ? s : null;
}
export function parseSpent(v) {
    if (!isObj(v) || Object.keys(v).length > 64)
        return null;
    const out = {};
    for (const [k, x] of Object.entries(v)) {
        if (!SPECIAL_KEY_RE.test(k) || !isObj(x) || Object.keys(x).some((f) => f !== 'uses' && f !== 'ready' && f !== 'recharge'))
            return null;
        if (x.uses !== undefined && !isInt(x.uses, 0, 1000))
            return null;
        if (x.ready !== undefined && !isInt(x.ready, 0, 1_000_000))
            return null;
        const r = x.recharge;
        if (r !== undefined && !(isObj(r) && isInt(r.from, 1, 100) && isInt(r.sides, 2, 100) && isText(r.label, 100)))
            return null;
        out[k] = { ...(x.uses !== undefined ? { uses: x.uses } : {}), ...(x.ready !== undefined ? { ready: x.ready } : {}), ...(r ? { recharge: { from: r.from, sides: r.sides, label: r.label } } : {}) };
    }
    return out;
}
export const copySpent = (s) => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, { ...v }]));
/** The bar's key of an ability; a template's `cast` names it with `SPECIAL_REF` before it. */
const SPECIAL_KEY_RE = /^[a-z_]+(-\d+){0,2}$/;
export const SPECIAL_REF = 'special:';
export const SPECIAL_LIMITS = { key: 40 };
const capital = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const typeWords = (t) => t.replace(/_/g, ' ');
const abilityWords = (a) => capital(a);
const diceParts = (d) => { const m = DICE_RE.exec(d); return { count: Number(m[1]), sides: Number(m[2]), bonus: Number(m[3] ?? 0) }; };
const saveLabel = (saves, key) => saves.find((x) => x.key === key)?.label ?? capital(key);
/** Everything the bar offers of a stat block's abilities. `squares` turns the system's distance into squares. */
export function specialActions(s, rules, saves, squares) {
    const out = [];
    (s.breath ?? []).forEach((b, i) => {
        const key = `breath-${i}`;
        const r = b.recharge;
        const dmg = `${b.damage}${b.type ? ` ${typeWords(b.type)}` : ''}`;
        const saveLine = `${saveLabel(saves, b.save)} ${b.on_save === 'none' ? 'negates' : 'half'}`;
        const back = r?.per_day ? `${r.per_day}/day` : r?.rounds ? `every ${r.rounds} rounds` : `every ${r?.dice ?? `${rules.breathRecharge.count}d${rules.breathRecharge.sides}`} rounds`;
        const recharge = r?.per_day ? undefined : r?.rounds ? { rounds: r.rounds } : r?.dice ? diceParts(r.dice) : { ...rules.breathRecharge, bonus: 0 };
        out.push({
            key, label: s.breath.length > 1 ? `Breath weapon ${i + 1}` : 'Breath weapon', detail: `${b.feet}-ft. ${b.shape} · ${dmg} · ${saveLine} DC ${b.dc} · ${back}`,
            spell: { key: `${SPECIAL_REF}${key}`, name: 'Breath weapon', shape: b.shape, size: Math.max(1, squares(b.feet)), saveLine, dc: b.dc, damage: dmg, castingTime: 'standard' },
            ...(r?.per_day ? { perDay: r.per_day } : {}), ...(recharge ? { recharge } : {}),
        });
    });
    (s.poison ?? []).forEach((p, i) => out.push({ key: `poison-${i}`, label: 'Poison', detail: `${saveLabel(saves, p.save)} DC ${p.dc} · ${p.initial.dice} ${abilityWords(p.initial.ability)}, then ${p.secondary.dice} ${abilityWords(p.secondary.ability)}`, target: true }));
    if (s.constrict)
        out.push({ key: 'constrict', label: 'Constrict', detail: hitWords(s.constrict), target: true });
    if (s.rend)
        out.push({ key: 'rend', label: 'Rend', detail: hitWords(s.rend), target: true });
    if (s.energy_drain)
        out.push({ key: 'energy_drain', label: 'Energy drain', detail: `${s.energy_drain.levels} negative level${s.energy_drain.levels === 1 ? '' : 's'}`, target: true });
    (s.ability ?? []).forEach((a, i) => out.push({ key: `ability-${i}`, label: `${abilityWords(a.ability)} ${a.how}`, detail: `${a.dice}${a.dc ? ` · DC ${a.dc}` : ''}`, target: true }));
    if (s.fear_aura) {
        const f = s.fear_aura;
        const saveLine = `${saveLabel(saves, rules.auraSave)} negates`;
        out.push({ key: 'fear_aura', label: capital(f.label), detail: `${f.feet} ft. · ${saveLine} DC ${f.dc}`, spell: { key: `${SPECIAL_REF}fear_aura`, name: capital(f.label), shape: 'burst', size: Math.max(1, squares(f.feet)), saveLine, dc: f.dc, castingTime: 'free' } });
    }
    if (s.pounce)
        out.push({ key: 'pounce', label: 'Pounce', detail: 'A full attack at the end of a charge' });
    (s.spell_like?.groups ?? []).forEach((g, gi) => g.spells.forEach((x, si) => out.push({
        key: `spell_like-${gi}-${si}`, label: capital(x.name), detail: [g.per_day ? `${g.per_day}/day` : 'At will', x.dc ? `DC ${x.dc}` : '', s.spell_like.caster_level ? `Caster level ${s.spell_like.caster_level}` : ''].filter(Boolean).join(' · '),
        ...(g.per_day ? { perDay: g.per_day } : {}),
    })));
    return out;
}
/** A spell-like ability as a cast reads it, by its bar key: the spell's name, its DC and the stat block's caster level, null where it prints none. */
export function spellLikeOf(s, key) {
    const m = /^spell_like-(\d+)-(\d+)$/.exec(key);
    const x = m ? s.spell_like?.groups[Number(m[1])]?.spells[Number(m[2])] : undefined;
    return x ? { name: x.name, dc: x.dc ?? null, casterLevel: s.spell_like.caster_level ?? null } : null;
}
/** A stat block's save options on the bar (§3.6): a template that resolves the save, a one-square template for a target picked. */
export function saveOptionActions(options, combat) {
    const per = combat.distance?.perSquare ?? null;
    const squares = (feet) => Math.max(1, per ? Math.round(feet / per) : 1);
    return options.map((o, i) => {
        const key = `save-${i}`;
        const saveLine = `${saveLabel(combat.saves?.list ?? [], o.save)} ${o.onSave === 'none' ? 'negates' : 'half'}`;
        const reach = o.shape && o.feet ? `${o.feet}-ft. ${o.shape}` : 'At a target';
        return {
            key, label: o.name, detail: [reach, o.damage, `${saveLine} DC ${o.dc}`, o.recharge ? `Recharge ${o.recharge.from}${o.recharge.from < o.recharge.sides ? `–${o.recharge.sides}` : ''}` : ''].filter(Boolean).join(' · '),
            spell: { key: `${SPECIAL_REF}${key}`, name: o.name, ...(o.shape && o.feet ? { shape: o.shape, size: squares(o.feet) } : { shape: 'rect', size: 1, size2: 1 }), saveLine, dc: o.dc, ...(o.damage ? { damage: o.damage } : {}), castingTime: 'standard' },
            ...(o.recharge ? { recharge: o.recharge } : {}),
        };
    });
}
/** What a stat block puts on the DM's bar: its `specials` on a system that reads them, then its save options on any. Null: nothing. */
export function blockActions(block, combat) {
    const s = combat.specials ? parseSpecials(block.specials) : null;
    const options = block.saveOptions ?? [];
    if (!s && !options.length)
        return null;
    return { specials: [...(s ? tokenSpecials(s, combat) : []), ...saveOptionActions(options, combat)], ...(s ? { parsed: s } : {}) };
}
/** The bar's list for a stat block, by the table's rules. Empty on a system with no `specials`. */
export function tokenSpecials(s, combat) {
    if (!combat.specials)
        return [];
    const per = combat.distance?.perSquare ?? null;
    return specialActions(s, combat.specials, combat.saves?.list ?? [], (feet) => (per ? Math.round(feet / per) : 1));
}
const hitWords = (h) => `${h.damage}${h.extra ? ` plus ${h.extra.dice}${h.extra.type ? ` ${typeWords(h.extra.type)}` : ''}` : ''}${h.of ? ` (${h.of})` : ''}`;
/** How many of a count a day are left, and the rounds until a recharging one is ready (0: ready). */
export function specialState(a, spent, round) {
    const s = spent?.[a.key];
    return { left: a.perDay !== undefined ? Math.max(0, a.perDay - (s?.uses ?? 0)) : null, wait: Math.max(0, (s?.ready ?? 0) - round), recharging: !!s?.recharge };
}
/** The token's record after using it: one more use, and the round it is ready again, with the rounds thrown. */
export function spendSpecial(a, spent, round, rng) {
    const was = spent?.[a.key] ?? {};
    let wait = 0;
    if (a.recharge && !('from' in a.recharge)) {
        if ('rounds' in a.recharge)
            wait = a.recharge.rounds;
        else {
            for (let i = 0; i < a.recharge.count; i += 1)
                wait += rng(a.recharge.sides);
            wait += a.recharge.bonus;
        }
    }
    const die = a.recharge && 'from' in a.recharge ? { from: a.recharge.from, sides: a.recharge.sides, label: a.label } : null;
    const next = { ...(a.perDay !== undefined ? { uses: (was.uses ?? 0) + 1 } : {}), ...(wait ? { ready: round + wait } : {}), ...(die ? { recharge: die } : {}) };
    return { spent: { ...(spent ?? {}), ...(Object.keys(next).length ? { [a.key]: next } : {}) }, wait };
}
/** A creature's turn starts: each recharging action's die, what is ready dropped from the record, and the DM's line for each. */
export function rollRecharges(spent, rng) {
    const out = {};
    const lines = [];
    for (const [k, s] of Object.entries(spent)) {
        if (!s.recharge) {
            out[k] = s;
            continue;
        }
        const face = rng(s.recharge.sides);
        const ready = face >= s.recharge.from;
        lines.push(`${s.recharge.label}: ${face}${ready ? ', ready' : ', not ready'}`);
        if (!ready) {
            out[k] = s;
            continue;
        }
        const { recharge: _back, ...rest } = s;
        if (Object.keys(rest).length)
            out[k] = rest;
    }
    return { spent: out, lines };
}
// ── As rule blocks ──────────────────────────────────────────────────────────────────────────────────────────────────
const HIT_ROLES = [{ key: 'attacker', label: 'Attacker', pick: 'trigger' }, { key: 'target', label: 'Target', pick: 'trigger' }];
const TURN_ROLES = [{ key: 'creature', label: 'Creature', pick: 'trigger' }];
const block = (id, name, when, roles, nodes) => ({ id, version: 1, kind: 'entry', name, roles, nodes, when });
/** Dice as a block's value. */
const value = (d) => { const p = diceParts(d); return { sum: [{ dice: { count: p.count, sides: p.sides } }, p.bonus] }; };
const saveEnd = (dice, role, save, dc, then) => ({
    branches: ['success', 'failure'], resolved_by: 'rules', judge: 'save', against: dc, then,
    value: { sum: [{ dice }, { direct: { source: 'sheet', role, purpose: 'saving_throw', which: save } }] },
});
const askEnd = (ask, then) => ({ branches: ['yes', 'no'], resolved_by: 'dm', ask: ask.slice(0, 200), then: { yes: then } });
const report = (onto, label, d) => ({ branches: [], resolved_by: 'rules', resolves: { kind: 'report', onto, label: label.slice(0, 60), value: value(d) } });
/** Ability damage or drain: it lowers the score on a system that says how (`abilityLoss`), else the number is reported. */
const lowers = (onto, ability, how, d, rules) => {
    const key = rules.abilityLoss ? abilityKeyOf(rules.abilityLoss, ability) : null;
    const label = `${rules.abilityLoss && key ? rules.abilityLoss.abilities[key].label : abilityWords(ability)} ${how}`;
    return key ? { branches: [], resolved_by: 'rules', resolves: { kind: 'ability', onto, key, how, label: label.slice(0, 60), value: value(d) } } : report(onto, label, d);
};
/** Parsed from an effect (encounter.ts). */
export function parsePoisonSecond(v) {
    if (!isObj(v) || !isText(v.name, 80) || !isText(v.save, 40) || !isInt(v.dc, 1, 100) || !isDice(v.dice) || !isText(v.ability, 12))
        return null;
    return { name: v.name, save: v.save, dc: v.dc, dice: v.dice, ability: v.ability };
}
export const poisonBlock = (p, i, carrier, saves, dice, rules) => ({
    ...block(`special_poison_${i}`, 'Poison', 'hits', HIT_ROLES, [
        { id: 'n1', end: askEnd(`Poison? ${saveLabel(saves, p.save)} DC ${p.dc}: ${p.initial.dice} ${abilityWords(p.initial.ability)}, then ${p.secondary.dice} ${abilityWords(p.secondary.ability)}`, 'n2') },
        { id: 'n2', end: saveEnd(dice, 'target', p.save, p.dc, { failure: 'n3' }) },
        { id: 'n3', end: lowers('target', p.initial.ability, 'damage', p.initial.dice, rules) },
    ]),
    timed: { label: `Poison: ${p.secondary.dice} ${abilityWords(p.secondary.ability)}, DC ${p.dc}`, rounds: rules.poisonRounds, second: { name: carrier.slice(0, 80), save: p.save, dc: p.dc, dice: p.secondary.dice, ability: p.secondary.ability } },
});
export const poisonSecondBlock = (s, effect, saves, dice, rules) => block(`special_poison_second_${effect.replace(/[^a-z0-9]/g, '').slice(0, 24)}`, 'Poison', 'turn_start', TURN_ROLES, [
    { id: 'n1', end: askEnd(`${s.name}'s poison, its second save? ${saveLabel(saves, s.save)} DC ${s.dc}: ${s.dice} ${abilityWords(s.ability)}`, 'n2') },
    { id: 'n2', end: saveEnd(dice, 'creature', s.save, s.dc, { failure: 'n3' }) },
    { id: 'n3', end: lowers('creature', s.ability, 'damage', s.dice, rules) },
]);
/** A stored condition row's: the save and its DC. */
export function parseRetry(v) {
    return isObj(v) && isText(v.save, 40) && isInt(v.dc, 1, 100) ? { save: v.save, dc: v.dc } : null;
}
/** Parsed from an effect (encounter.ts). */
export function parseEffectRetry(v) {
    const r = parseRetry(v);
    return r && isObj(v) && isText(v.name, 80) ? { ...r, name: v.name } : null;
}
/** The save at the end of a turn: the DM asked nothing, a success takes the effect `ends` off and says so. */
export const retryBlock = (r, label, effect, dice) => ({
    ...block(`retry_save_${effect.replace(/[^a-z0-9]/g, '').slice(0, 24)}`, r.name, 'turn_end', TURN_ROLES, [
        { id: 'n1', end: saveEnd(dice, 'creature', r.save, r.dc, { success: 'n2' }) },
        { id: 'n2', end: { branches: [], resolved_by: 'rules', resolves: { kind: 'note', onto: 'creature', label: `Saved: ${label} ends`.slice(0, 60) } } },
    ]),
    ends: effect,
});
/** Hit points at the creature's turn start, with no question: the log's line and the DM's Apply as any heal. */
export const healBlock = (name, amount) => block(`special_${name.toLowerCase().replace(/ /g, '_')}`, name, 'turn_start', TURN_ROLES, [
    { id: 'n1', end: { branches: [], resolved_by: 'rules', resolves: { kind: 'heal', onto: 'creature', value: amount } } },
]);
/** The block's damage on a target, for a Constrict or a Rend the DM aims from the bar. */
export const damageBlock = (key, name, h) => block(`special_${key}`, name, 'hits', HIT_ROLES, [
    { id: 'n1', end: { branches: [], resolved_by: 'rules', judge: 'damage', value: value(h.damage), ...(h.extra ? { extra: value(h.extra.dice) } : {}), resolves: { kind: 'damage', onto: 'target' } } },
]);
export const drainBlock = (d, rules) => block('special_energy_drain', 'Energy drain', 'hits', HIT_ROLES, [
    { id: 'n1', end: askEnd(`Energy drain? ${d.levels} negative level${d.levels === 1 ? '' : 's'}`, 'n2') },
    { id: 'n2', end: { branches: ['done', 'never'], resolved_by: 'rules', judge: 'compare', value: d.levels, against: 0, resolves: { kind: 'report', onto: 'target', label: 'Negative levels', value: d.levels }, then: { done: 'n3' } } },
    { id: 'n3', end: { branches: [], resolved_by: 'rules', resolves: { kind: 'condition', onto: 'target', key: rules.drainCondition } } },
]);
export const abilityBlock = (a, i, saves, dice, save, rules) => {
    const label = `${abilityWords(a.ability)} ${a.how}`;
    return block(`special_ability_${i}`, label, 'hits', HIT_ROLES, a.dc
        ? [
            { id: 'n1', end: askEnd(`${label}? ${a.dice}, ${saveLabel(saves, save)} DC ${a.dc}`, 'n2') },
            { id: 'n2', end: saveEnd(dice, 'target', save, a.dc, { failure: 'n3' }) },
            { id: 'n3', end: lowers('target', a.ability, a.how, a.dice, rules) },
        ]
        : [
            { id: 'n1', end: askEnd(`${label}? ${a.dice}`, 'n3') },
            { id: 'n3', end: lowers('target', a.ability, a.how, a.dice, rules) },
        ]);
};
/** A follow-on maneuver after a hit (a trip): asked of the DM; Yes makes it, with no touch attack, attack of opportunity or try back. */
export const followBlock = (kind, maneuver, label) => block(`special_${kind}`, label, 'hits', HIT_ROLES, [
    { id: 'n1', end: askEnd(`${label} as well?`, 'n2') },
    { id: 'n2', end: { branches: [], resolved_by: 'rules', resolves: { kind: 'maneuver', onto: 'attacker', key: maneuver } } },
]);
/** Whether a hit with this attack is one an ability comes on: any of a block that does not say which. */
export function comesOn(list, attack) {
    if (!list?.length || !attack)
        return true;
    const stem = (w) => w.toLowerCase().replace(/s$/, '').trim();
    const a = stem(attack);
    return list.some((w) => { const s = stem(w); return a === s || a.includes(s) || s.includes(a); });
}
/** The abilities that come on a hit, as blocks: each asks the DM, so a hit with none of them changes nothing. */
export function hitBlocks(s, attack, carrier, rules, saves, dice) {
    const out = [];
    (s.poison ?? []).forEach((p, i) => { if (comesOn(p.on, attack))
        out.push(poisonBlock(p, i, carrier, saves, dice, rules)); });
    if (s.energy_drain && comesOn(s.energy_drain.on, attack))
        out.push(drainBlock(s.energy_drain, rules));
    (s.ability ?? []).forEach((a, i) => { if (comesOn(a.on, attack))
        out.push(abilityBlock(a, i, saves, dice, saves[0]?.key ?? '', rules)); });
    if (s.trip && rules.follow.trip && comesOn(s.trip.on, attack))
        out.push(followBlock('trip', rules.follow.trip, 'Trip'));
    return out;
}
/** The block a bar action at a target runs; null for one that is not aimed. */
export function actionBlock(key, s, carrier, rules, saves, dice) {
    const [kind, n] = key.split('-');
    const i = Number(n ?? 0);
    if (kind === 'poison' && s.poison?.[i])
        return poisonBlock(s.poison[i], i, carrier, saves, dice, rules);
    if (kind === 'constrict' && s.constrict)
        return damageBlock('constrict', 'Constrict', s.constrict);
    if (kind === 'rend' && s.rend)
        return damageBlock('rend', 'Rend', s.rend);
    if (kind === 'energy_drain' && s.energy_drain)
        return drainBlock(s.energy_drain, rules);
    if (kind === 'ability' && s.ability?.[i])
        return abilityBlock(s.ability[i], i, saves, dice, saves[0]?.key ?? '', rules);
    return null;
}
//# sourceMappingURL=specials.js.map